/**
 * Simple debug overlay system for showing temporary messages in top-left corner
 */
export class DebugOverlay {
    private static instance: DebugOverlay | null = null;
    private container: HTMLElement | null = null;
    private messages: Map<string, { element: HTMLElement; timeout: number }> = new Map();
    private readonly MAX_MESSAGES = 20;
    private readonly MESSAGE_DURATION = 5000; // 5 seconds
    private messageCounter = 0;

    private constructor() {
        this.createContainer();
    }

    public static getInstance(): DebugOverlay {
        if (!DebugOverlay.instance) {
            DebugOverlay.instance = new DebugOverlay();
        }
        return DebugOverlay.instance;
    }

    private createContainer(): void {
        this.container = document.createElement('div');
        this.container.id = 'debug-overlay';
        this.container.style.cssText = `
            position: fixed;
            top: 10px;
            left: 10px;
            z-index: 10000;
            pointer-events: none;
            font-family: 'Courier New', monospace;
            font-size: 12px;
            color: white;
            text-shadow: 1px 1px 2px rgba(0,0,0,0.8);
            max-width: 300px;
        `;
        document.body.appendChild(this.container);
    }

    public showMessage(message: string, id?: string): void {
        if (!this.container) return;

        // Use provided ID or generate one
        const messageId = id || `msg_${this.messageCounter++}`;

        // Remove existing message with same ID
        if (this.messages.has(messageId)) {
            this.removeMessage(messageId);
        }

        // Remove oldest message if we're at max capacity
        if (this.messages.size >= this.MAX_MESSAGES) {
            const oldestId = Array.from(this.messages.keys())[0];
            this.removeMessage(oldestId);
        }

        // Create message element
        const messageElement = document.createElement('div');
        messageElement.style.cssText = `
            background: rgba(0,0,0,0.7);
            padding: 4px 8px;
            margin-bottom: 2px;
            border-radius: 3px;
            border-left: 3px solid #00ff00;
            animation: debugFadeIn 0.2s ease-out;
        `;
        messageElement.textContent = message;

        // Add CSS animation keyframes if not already added
        if (!document.querySelector('#debug-overlay-styles')) {
            const style = document.createElement('style');
            style.id = 'debug-overlay-styles';
            style.textContent = `
                @keyframes debugFadeIn {
                    from { opacity: 0; transform: translateX(-10px); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes debugFadeOut {
                    from { opacity: 1; transform: translateX(0); }
                    to { opacity: 0; transform: translateX(-10px); }
                }
            `;
            document.head.appendChild(style);
        }

        this.container.appendChild(messageElement);

        // Set up auto-removal
        const timeout = window.setTimeout(() => {
            this.removeMessage(messageId);
        }, this.MESSAGE_DURATION);

        this.messages.set(messageId, { element: messageElement, timeout });
    }

    private removeMessage(id: string): void {
        const messageData = this.messages.get(id);
        if (messageData && this.container) {
            // Add fade out animation
            messageData.element.style.animation = 'debugFadeOut 0.2s ease-out';
            
            // Remove after animation
            setTimeout(() => {
                if (this.container && messageData.element.parentNode === this.container) {
                    this.container.removeChild(messageData.element);
                }
            }, 200);

            clearTimeout(messageData.timeout);
            this.messages.delete(id);
        }
    }

    public clearAllMessages(): void {
        for (const id of this.messages.keys()) {
            this.removeMessage(id);
        }
    }

    public destroy(): void {
        this.clearAllMessages();
        if (this.container) {
            document.body.removeChild(this.container);
            this.container = null;
        }
        
        // Remove styles
        const styles = document.querySelector('#debug-overlay-styles');
        if (styles) {
            document.head.removeChild(styles);
        }
        
        DebugOverlay.instance = null;
    }
}

// Convenience function for easy access
export function showDebugMessage(message: string, id?: string): void {
    DebugOverlay.getInstance().showMessage(message, id);
} 